import * as cn from 'classnames'
import { FC, ReactNode, useMemo } from 'react'
import themes from 'styles/base/themes.styl'
import styles from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification.styl'
import SvgIcon from 'components/atoms/SvgIcon'
import CrossSvg from 'icons/cross.svg'
import { Theme, ThemeStyle } from 'interfaces/component'
import Flex from 'components/atoms/Flex/Flex'
import { IconCircled } from 'components/features/pfm/IconCircled/IconCircled'
import { getSvgByFilename } from 'helpers/mediaHelpers'

export type FloatingPaneNotificationProps = {
  text?: string
  messages?: string[] | ReactNode[]
  onDismiss?: () => void
  theme?: Theme
  style?: ThemeStyle
  iconSlug?: string
  iconCircled?: boolean
  actionChild?: ReactNode
  positionAbsolute?: boolean
  className?: string
  small?: boolean
}

const FloatingPaneNotification: FC<FloatingPaneNotificationProps> = (props) => {
  const {
    children,
    onDismiss,
    actionChild,
    text,
    iconSlug,
    iconCircled,
    messages,
    positionAbsolute,
    theme = 'lightRed',
    style = 'notification',
    small,
  } = props

  const dissmissChild = useMemo(() => !!onDismiss && (
    <button onClick={ onDismiss } type='button' className={ styles.dismissButton } role='dismiss-button'>
      <SvgIcon src={ CrossSvg } />
    </button>
  ), [onDismiss])

  const icon = theme === 'lightRed' ? 'warning' : iconSlug

  const iconChild = useMemo(() => !!icon && (
    <i className={ styles.icon }>
      { iconCircled ? <IconCircled iconSlug={ icon } theme={ theme } /> : <SvgIcon src={ getSvgByFilename(icon) } /> }
    </i>
  ), [icon, iconCircled, theme])

  const contentChild = useMemo(() => {
    if (!messages && !text && !children) {
      return null
    } else if (!messages) {
      return text || children
    } else if (messages instanceof Array) {
      return (
        <ul>
          { messages.map((message, idx) => <li key={ idx }>{ message }</li>) }
        </ul>
      )
    }
    return messages
  }, [messages, text, children])

  if (!contentChild) {
    return null
  }

  const classNames = cn(
    styles.floatingPaneNotification,
    themes[style],
    themes[theme], {
      [styles.small]: !!small,
      [styles.noPadding]: style === 'textOnly',
      [styles.positionAbsolute]: !!positionAbsolute,
      [styles.verticalAlign]: !!actionChild,
    }
  )

  const resultingChildren = (
    <>
      <Flex horizontal verticallyCenter gap='tiny' fullWidth className={ styles.content }>
        { iconChild }
        { contentChild }
      </Flex>
      { actionChild || dissmissChild }
    </>
  )

  return !!small ? (
    <small className={ classNames }>{ resultingChildren }</small>
  ) : (
    <div className={ classNames }>{ resultingChildren }</div>
  )
}

export default FloatingPaneNotification
